import { axiosClient } from "../api/axiosClient";

/**
 * Minimal shape for bank account options returned by the API.
 * Extend this as needed to match the backend contract.
 */
export interface BankAccountOption {
  id: string;
  name: string;
  code?: string;
  [k: string]: unknown;
}

/**
 * Full BankAccount shape based on example payloads.
 */
export interface BankAccount {
  id: string;
  name: string;
  account: string;
  agency: string;
  bankAccountOption: BankAccountOption;
  initialBalance: string;
  currentBalance: string;
  pixKey: string | null;
  pixKeyType: "EMAIL" | "PHONE" | "CPF" | "CNPJ" | null;
  accountType: "BANK" | "CASH" | string;
  createdAt: string; // ISO timestamp
  updatedAt: string; // ISO timestamp
  [k: string]: unknown;
}

// new: payload for creating a bank account
export interface CreateBankAccountPayload {
  terreiroId?: string;
  mediumId?: string;
  name: string;
  account?: string;
  agency?: string;
  bankAccountOptionId?: string;
  initialBalance?: number;
  currentBalance?: number;
  pixKey?: string | null;
  pixKeyType?: BankAccount["pixKeyType"];
  accountType?: BankAccount["accountType"];
  [k: string]: unknown;
}

/**
 * Payload used to update a bank account.
 * Keep fields optional to allow partial updates.
 * Prefer sending bankAccountOptionId instead of full object when updating.
 */
export interface UpdateBankAccountPayload {
  name?: string;
  account?: string;
  agency?: string;
  bankAccountOptionId?: string;
  initialBalance?: string;
  currentBalance?: string;
  pixKey?: string | null;
  pixKeyType?: BankAccount["pixKeyType"];
  accountType?: BankAccount["accountType"];
  [k: string]: unknown;
}

export const bankRepository = {
  async createBankAccount(
    payload: CreateBankAccountPayload
  ): Promise<BankAccount> {
    const { data } = await axiosClient.post<BankAccount>(
      "/bank-account",
      payload,
      { withCredentials: true }
    );
    return data;
  },

  /**
   * Fetch list of bank account options.
   * Endpoint is relative to /v1/ per project conventions.
   */
  async getBankAccountsOptions(): Promise<BankAccountOption[]> {
    const { data } = await axiosClient.get<BankAccountOption[]>(
      "/bank-account-options",
      { withCredentials: true }
    );
    return data;
  },

  /**
   * Fetch a single bank account by id.
   * GET /v1/bank-account/:id
   */
  async getBankAccount(id: string): Promise<BankAccount> {
    const { data } = await axiosClient.get<BankAccount>(
      `/bank-account/${encodeURIComponent(id)}`,
      { withCredentials: true }
    );
    return data;
  },

  /**
   * Update a bank account.
   * PUT /v1/bank-account/:id
   */
  async updateBankAccount(
    id: string,
    payload: UpdateBankAccountPayload
  ): Promise<BankAccount> {
    const { data } = await axiosClient.put<BankAccount>(
      `/bank-account/${encodeURIComponent(id)}`,
      payload,
      { withCredentials: true }
    );
    return data;
  },
};
